import Vue from 'vue';
import { Component, Watch } from 'vue-property-decorator';
import { CmsLink, CustomerProfile, CmsLinkLocation, CmsLinkTarget, OrderWithContext, OrderMeal } from '@/models';
import { HeaderOptions, HeaderFlags, CountryCode } from '@/store/ui';
import { ui, countries } from '@/constants';
import { shouldShowLink, isSustainabilityEnabled, isLoyaltyEnabled } from '@/helpers';
import { AnalyticsEventCategory, AnalyticsEventAction, AnalyticsEventLabel } from '@/services/google/google-analytics.services';
import { AccountPageLoadEvent } from '@/services/analytics/events';

@Component({
  name: 'CmgAccountModal',
})
export default class CmgAccountModal extends Vue {
  profileWatcher?: () => void;

  get medallionFluted(): string {
    return this.$appStore.state.cms.images.logos.medallionFluted.default;
  }

  get countryName(): string {
    return this.$appStore.state.ui.countryProfile.name;
  }

  get customer(): CustomerProfile | null {
    const profile = this.$appStore.state.customer.profile;
    return profile && !profile.isGuest ? profile : null;
  }

  get isMobile(): boolean {
    return this.$appStore.state.ui.clientWidth <= ui.breakpoints.phoneLgMax;
  }

  get isDesktop(): boolean {
    return this.$appStore.state.ui.clientWidth >= ui.breakpoints.desktop;
  }

  get isTablet(): boolean {
    const width = this.$appStore.state.ui.clientWidth;
    return width >= ui.breakpoints.tablet && width <= ui.breakpoints.tabletMax;
  }

  get closeButtonImageUrl(): string {
    return this.$appStore.state.cms.images.buttons.xDarkBrown.default;
  }

  get visible(): boolean {
    const visible = this.$appStore.state.ui.modals.account.visible;
    if (visible) {
      this.$services.googleAnalytics.sendCustomEvent({
        category: AnalyticsEventCategory.RewardsAccount,
        action: AnalyticsEventAction.AccountMenuModalViewed,
        label: AnalyticsEventLabel.AccountMenuModalViewed,
      });
    }
    return visible;
  }

  get headerOptions(): HeaderOptions {
    return this.$appStore.state.ui.header.options;
  }

  get headerFlags(): HeaderFlags {
    return this.headerOptions.flags;
  }

  get allHeaderFlags() {
    return HeaderFlags;
  }

  get headerLinks(): CmsLink[] {
    return this.$appStore.state.cms.header.links.filter(shouldShowLink(this.siteCountryCode, this.profileCountryCode, !!this.isCustomerLoggedIn));
  }

  get profileCountryCode(): string {
    return this.customer ? this.customer.country || CountryCode.US : '';
  }

  get order(): OrderWithContext | null {
    return this.$services.order.getCurrentOrder();
  }

  get meals(): OrderMeal[] {
    return (this.order && this.order.order.meals) || [];
  }

  get currentCountryDisplayName(): string {
    switch (this.siteCountryCode) {
      case CountryCode.CA:
        return 'CANADA';
      case CountryCode.US:
      default:
        return 'U.S.';
    }
  }

  get temporaryCountryModeDisplayName(): string {
    switch (this.profileCountryCode) {
      case CountryCode.CA:
        return 'Canada';
      case CountryCode.US:
      default:
        return 'U.S.';
    }
  }

  get siteCountryCode(): string {
    return this.$appStore.state.ui.countryProfile.countryCode;
  }  

  get isCustomerLoggedIn(): boolean | undefined {
    return this.$services.customer.isLoggedIn();
  }

  get isServiceOnline(): boolean {
    return !this.$appStore.state.reward.unreachable;
  }

  get isEnrolledInLoyalty() {
    return this.$services.loyalty.isEnrolledInLoyalty(this.customer);
  }

  get isLoyaltyEnabled(): boolean {
    return isLoyaltyEnabled(this.siteCountryCode);
  }

  get isLoyaltyEligible() {
    return this.$services.loyalty.isLoyaltyEligible(this.customer);
  }
 
  get shouldShowSustainability(): boolean {
    return isSustainabilityEnabled(this.siteCountryCode);
  }

  get temporaryCountryMode(): boolean {
    return !!this.customer && this.profileCountryCode !== this.siteCountryCode;
  }

  get background(): string {
    if (this.isLoyaltyEnabled) {
      return 'white';
    }
    return 'grey-medium';
  }

  get returnToRouteNavItem(): CmsLink {
    const navItem: CmsLink = {
      type: 'text',
      location: CmsLinkLocation.Internal,
      text: this.headerOptions.routeText || '',
      url: this.headerOptions.route || '',
      target: CmsLinkTarget.Self,
    };
    return navItem;
  }

  mounted() {
    this.profileWatcher = this.$appStore.watch(state => state.customer.profile, this.getPoints);
  }

  destroyed() {
    if (this.profileWatcher) {
      this.profileWatcher();
    }
  }

  @Watch('visible', { immediate: true })
  async visibleChanged(newVisible: boolean) {
    if (newVisible) {
      this.$services.analytics.send(new AccountPageLoadEvent('home'));
    }
  }

  async getPoints() {    
    if (!this.customer || !this.isLoyaltyEligible) {
      return;
    }

    try {
      await this.$services.loyalty.refreshPoints(true);
    } catch (e) {
      this.$services.logger.error('Something went wrong retrieving rewards points: ', undefined, e);
    }
  }

  close() {
    this.$services.ui.setModalVisibility('account', false);
    this.$services.ui.focusBack();
  }

  changeCountryProfile() {
    if (this.meals.length > 0) {
      this.$services.ui.setModalVisibility('lostBagModal', true);
    } else {
      this.$services.ui.setCountryProfile(countries[this.profileCountryCode.toLowerCase()], true);
    }
  }
}
